// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"

// Import Trix editor - must come before ActionText
import "trix"
import "@rails/actiontext"

// Import ActionCable for real-time features
import "@rails/actioncable"
import "./channels"

// Import Honeybadger for error reporting
import Honeybadger from './honeybadger_config'
window.Honeybadger = Honeybadger

// Import Hotwire Combobox
import "@josefarias/hotwire_combobox"

// Initialize Action Cable
import { createConsumer } from "@rails/actioncable"
window.App = window.App || {};
window.App.cable = createConsumer();

// Lazy load heavy dependencies only when needed
window.loadFlowbite = async () => {
  if (!window.Flowbite) {
    await import('flowbite/dist/flowbite.turbo.js')
  }
}

window.loadFlowbiteDatepicker = async () => {
  if (!window.FlowbiteDatepicker) {
    const [{ default: Datepicker }, { default: DateRangePicker }] = await Promise.all([
      import('flowbite-datepicker/Datepicker'),
      import('flowbite-datepicker/DateRangePicker')
    ])
    window.FlowbiteDatepicker = { Datepicker, DateRangePicker }
    return window.FlowbiteDatepicker
  }
  return window.FlowbiteDatepicker
}

window.loadFlatpickr = async () => {
  if (!window.flatpickr) {
    // Load Flatpickr JavaScript
    const { default: flatpickr } = await import("flatpickr")
    window.flatpickr = flatpickr
    
    // Dynamically load CSS files from CDN
    if (!document.querySelector('link[href*="flatpickr"]')) {
      const link1 = document.createElement('link')
      link1.rel = 'stylesheet'
      link1.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css'
      document.head.appendChild(link1)
      
      const link2 = document.createElement('link')
      link2.rel = 'stylesheet'
      link2.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/themes/light.css'
      document.head.appendChild(link2)
    }
    
    return flatpickr
  }
  return window.flatpickr
}

// Debug statement
console.log('Application.js loaded');

// Initialize Trix editors on page load and navigation
document.addEventListener('DOMContentLoaded', fixTrixIcons);
document.addEventListener('turbo:load', fixTrixIcons);
document.addEventListener('turbo:frame-load', fixTrixIcons);
document.addEventListener('turbo:render', fixTrixIcons);

// Function to initialize Trix editors
function fixTrixIcons() {
  console.log('Fixing Trix icons');
  
  // Trigger our controller for editors that have it
  const editorControllers = document.querySelectorAll('[data-controller="trix-editor"]');
  if (editorControllers.length > 0) {
    console.log(`Found ${editorControllers.length} Trix editor controllers`);
    editorControllers.forEach(el => {
      const event = new CustomEvent('trix-editor:reconnect');
      el.dispatchEvent(event);
    });
  }
  
  // Apply manual fixes for editors without our controller
  const trixButtons = document.querySelectorAll('trix-toolbar .trix-button');
  if (trixButtons.length > 0) {
    console.log(`Manually fixing ${trixButtons.length} Trix buttons`);
    
    // Map of button types to their text representation
    const iconMap = {
      'bold': 'B',
      'italic': 'I',
      'strike': 'S',
      'link': '🔗',
      'heading1': 'H1',
      'quote': '"',
      'code': '</>',
      'pre': '</>',
      'bullet': '•',
      'number': '1.',
      'decreaseNestingLevel': '←',
      'increaseNestingLevel': '→',
      'attachFiles': '📎',
      'undo': '↩',
      'redo': '↪'
    };
    
    trixButtons.forEach(button => {
      // Clear any text directly in the button
      Array.from(button.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = '';
        }
      });
      
      // Hide any spans that might contain text
      const spans = button.querySelectorAll('span:not(.trix-fallback-icon)');
      spans.forEach(span => {
        span.style.display = 'none';
      });
      
      // Get button attributes
      const attribute = button.getAttribute('data-trix-attribute');
      const action = button.getAttribute('data-trix-action');
      const key = attribute || action;
      
      if (key) {
        // Get or create fallback icon element
        let fallbackIcon = button.querySelector('.trix-fallback-icon');
        if (!fallbackIcon) {
          fallbackIcon = document.createElement('span');
          fallbackIcon.classList.add('trix-fallback-icon');
          button.appendChild(fallbackIcon);
        }
        
        // Position and style the fallback icon
        fallbackIcon.style.position = 'absolute';
        fallbackIcon.style.top = '0';
        fallbackIcon.style.left = '0';
        fallbackIcon.style.right = '0';
        fallbackIcon.style.bottom = '0';
        fallbackIcon.style.display = 'flex';
        fallbackIcon.style.alignItems = 'center';
        fallbackIcon.style.justifyContent = 'center';
        fallbackIcon.style.fontWeight = 'bold';
        fallbackIcon.style.fontSize = '14px';
        
        // Set icon content
        const buttonKey = key.replace(/-/g, '');
        if (iconMap[buttonKey]) {
          fallbackIcon.innerHTML = iconMap[buttonKey];
        } else {
          // Default fallback
          fallbackIcon.innerHTML = key.charAt(0).toUpperCase();
          console.log(`No icon mapping for button: ${key}`);
        }
        
        // Ensure button has relative positioning
        button.style.position = 'relative';
      }
    });
  }
  
  // Style the Cancel and Delete buttons in Trix
  fixTrixAttachmentButtons();
}

// Function to fix Trix attachment buttons (Cancel and Delete)
function fixTrixAttachmentButtons() {
  // Set up a mutation observer to catch dynamically added buttons
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && (
              node.classList.contains('trix-button--cancel') || 
              node.classList.contains('trix-button--delete')
            )) {
              styleButton(node);
            } else {
              const buttons = node.querySelectorAll('.trix-button--cancel, .trix-button--delete');
              buttons.forEach(styleButton);
            }
          }
        });
      }
    });
  });
  
  // Start observing the body
  observer.observe(document.body, { childList: true, subtree: true });
  
  // Style existing buttons
  document.querySelectorAll('.trix-button--cancel, .trix-button--delete').forEach(styleButton);
}

// Helper function to style a button
function styleButton(button) {
  if (button.classList.contains('styled')) return;
  
  console.log("Styling button:", button);
  button.classList.add('styled');
  
  if (button.classList.contains('trix-button--cancel')) {
    // Style for Cancel button
    button.style.backgroundColor = 'white';
    button.style.color = '#374151'; // gray-700
    button.style.border = '1px solid #d1d5db'; // gray-300
    button.style.borderRadius = '0.375rem';
    button.style.padding = '0.5rem 1rem';
    button.style.fontSize = '0.875rem';
    button.style.fontWeight = '500';
    button.style.marginRight = '0.5rem';
    button.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
    button.style.cursor = 'pointer';
    
    // Add hover effect
    button.addEventListener('mouseover', () => {
      button.style.backgroundColor = '#f9fafb'; // gray-50
    });
    button.addEventListener('mouseout', () => {
      button.style.backgroundColor = 'white';
    });
  } else if (button.classList.contains('trix-button--delete')) {
    // Style for Delete button
    button.style.backgroundColor = '#dc2626'; // red-600
    button.style.color = 'white';
    button.style.border = '1px solid transparent';
    button.style.borderRadius = '0.375rem';
    button.style.padding = '0.5rem 1rem';
    button.style.fontSize = '0.875rem';
    button.style.fontWeight = '500';
    button.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
    button.style.cursor = 'pointer';
    
    // Add hover effect
    button.addEventListener('mouseover', () => {
      button.style.backgroundColor = '#b91c1c'; // red-700
    });
    button.addEventListener('mouseout', () => {
      button.style.backgroundColor = '#dc2626'; // red-600
    });
  }
}

// Validate file attachments
addEventListener("trix-file-accept", event => {
  // Restrict by file size
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  if (event.file.size > maxFileSize) {
    event.preventDefault();
    alert("File size must be less than 10MB");
  }
});

// Listen for attachment additions to style buttons
addEventListener("trix-attachment-add", () => {
  setTimeout(() => {
    document.querySelectorAll('.trix-button--cancel, .trix-button--delete').forEach(styleButton);
  }, 100);
});
